import { useEffect, useRef, useState } from "react";
import { Languages } from "lucide-react";
import { LANGUAGES } from "@/utils/config/elevenlabs";

interface LanguagePopoverProps {
  brandColor: string;
  variant?: "popover" | "modal";
  subdued?: boolean;
  triggerPx?: number;
  /** Idle background override for the trigger button (replaces the default white). */
  idleBackgroundColor?: string | null;
  /** Icon color override for the trigger button (replaces brandColor). */
  iconColor?: string | null;
  /** Hides the built-in trigger button — open state is driven entirely by `open`/`onOpenChange` (e.g. from a "Languages" item inside another menu). */
  hideTrigger?: boolean;
  /** Controlled open state. Omit to let the component manage its own trigger + open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const TOP_LANGUAGE_CODES = ["en", "de", "es", "fr", "hi", "fil", "ja"];
const TOP_LANGUAGES = LANGUAGES.filter((l) => TOP_LANGUAGE_CODES.includes(l.code));
const MORE_LANGUAGES = LANGUAGES.filter((l) => !TOP_LANGUAGE_CODES.includes(l.code));

export const LanguagePopover = ({
  brandColor,
  variant = "modal",
  subdued = false,
  triggerPx = 44,
  idleBackgroundColor,
  iconColor,
  hideTrigger = false,
  open: controlledOpen,
  onOpenChange,
}: LanguagePopoverProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (v: boolean) => {
    setUncontrolledOpen(v);
    onOpenChange?.(v);
  };

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [setOpen]);

  return (
    <div ref={ref} className="relative">
      {!hideTrigger && (
        <button
          onClick={() => setOpen(!open)}
          className="inline-flex items-center justify-center rounded-full"
          style={{
            width: triggerPx,
            height: triggerPx,
            backgroundColor: idleBackgroundColor || "white",
            border: subdued ? "1px solid #E5EAF0" : `1px solid ${brandColor}33`,
            color: iconColor || (subdued ? "#7C8B9C" : brandColor),
            boxShadow: subdued ? "0 1px 3px rgba(15,23,42,0.06)" : "0 4px 10px rgba(0,0,0,0.08)",
          }}
          aria-label="Talk in any language"
          title="Talk in any language"
        >
          <Languages size={triggerPx <= 36 ? 16 : 20} />
        </button>
      )}

      {open &&
        (variant === "modal" ? (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/35"
            onClick={() => setOpen(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-white border border-gray-200 rounded-[14px] w-[360px] max-w-[90vw] max-h-[80vh] p-3.5"
              style={{ boxShadow: "0 20px 40px rgba(0,0,0,0.18)" }}
              role="dialog"
              aria-label="Languages"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-base text-gray-900">Talk in any language</div>
                <button
                  onClick={() => setOpen(false)}
                  className="bg-transparent border-none cursor-pointer text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="flex flex-wrap gap-2.5 mb-3">
                {TOP_LANGUAGES.map((lang) => (
                  <div
                    key={lang.code}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white text-gray-900 text-[0.9rem]"
                    style={{ border: `1px solid ${brandColor}33`, boxShadow: "0 2px 6px rgba(0,0,0,0.06)" }}
                  >
                    <span className="text-sm">{lang.flag || "🌐"}</span>
                    <span>{lang.name}</span>
                  </div>
                ))}
              </div>

              <div className="h-px bg-gray-100 my-2" />

              <div className="text-[0.82rem] text-gray-500 mb-1.5">More languages</div>
              <div
                className="brand-scroll max-h-[300px] overflow-y-auto"
                style={{ ["--brand-color" as any]: brandColor }}
              >
                {MORE_LANGUAGES.map((lang) => (
                  <div key={lang.code} className="flex items-center gap-2.5 py-2 px-1 border-b border-gray-100">
                    <span className="text-base">{lang.flag || "🌐"}</span>
                    <span className="text-[0.95rem] text-gray-700">{lang.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div
            className="absolute top-11 left-0 z-50 bg-white border border-gray-200 rounded-xl p-2.5 w-60"
            style={{ boxShadow: "0 12px 28px rgba(0,0,0,0.12)" }}
            role="dialog"
            aria-label="Languages"
          >
            <div className="font-semibold text-[0.85rem] mb-1.5 text-gray-700">Talk in any language</div>

            <div className="flex flex-wrap gap-1.5 mb-2">
              {TOP_LANGUAGES.map((lang) => (
                <div
                  key={lang.code}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white text-gray-900 text-[0.8rem]"
                  style={{ border: `1px solid ${brandColor}33`, boxShadow: "0 2px 5px rgba(0,0,0,0.05)" }}
                >
                  <span className="text-[13px]">{lang.flag || "🌐"}</span>
                  <span>{lang.name}</span>
                </div>
              ))}
            </div>

            <div className="h-px bg-gray-100 my-1.5" />

            <div className="text-[0.75rem] text-gray-500 mb-1">More languages</div>
            <div className="brand-scroll max-h-40 overflow-y-auto" style={{ ["--brand-color" as any]: brandColor }}>
              {MORE_LANGUAGES.map((lang) => (
                <div key={lang.code} className="flex items-center gap-2 py-1.5 px-0.5 border-b border-gray-100">
                  <span className="text-sm">{lang.flag || "🌐"}</span>
                  <span className="text-[0.9rem] text-gray-700">{lang.name}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
};

export default LanguagePopover;
